import React from 'react';
import { observer } from 'mobx-react';
import Loading from '../../../App/Components/Loading/Loading';
import { Neo } from '@singularsystems/neo-react';
import { TextConstants } from '../../../Common/TextConstants';
import { EnumHelper, ModalUtils, Validation } from '@singularsystems/neo-core';
import CurrencyComponent from '../../../Common/Components/Currency/CurrencyComponent';
import ManageClientAccountMappingVM from './ManageClientAccountMappingVM';
import { StatusDropDownItems } from '../Models/StatusDropDownItems';

interface IManageClientAccountComponentProps {
    viewModel: ManageClientAccountMappingVM;
    onBack: () => void;
    onSave: () => void;
}

@observer
export default class ManageClientAccountComponent extends React.Component<IManageClientAccountComponentProps> {
    public render() {
        const viewModel = this.props.viewModel;

        return (
            <React.Fragment>
                {(viewModel.taskRunner.isBusy) && <Loading />}
                <Neo.Card title={`${viewModel.clientAccountModel.editingClientAccount === false ? TextConstants.Titles.MapClientAccount : TextConstants.Titles.EditClientAccount}`}>
                    <Neo.Form 
                        onSubmit={() => this.props.onSave()} 
                        validationDisplayMode={Validation.DisplayMode.AfterSubmit}
                        model={viewModel.clientAccountModel}
                        onInvalid={this.onFormInvalid}
                        showSummaryModal>
                        {viewModel.clientAccountModel.clientNumber  === "" &&
                            <div className="pb-3">
                                <Neo.Checkbox
                                    bind={viewModel.clientAccountModel.meta.existingClient}
                                    label={TextConstants.Labels.ExistingClient}
                                    onBlur={() => viewModel.clearPopulatedFields()} />
                            </div>
                        }

                        {viewModel.clientAccountModel.existingClient === true &&
                            <div className="row">
                                <div className="col-lg-6 col-md-6">
                                    <Neo.FormGroup bindContext={viewModel.clientAccountModel.meta.clientId}
                                        label={TextConstants.Titles.Client}
                                        isReadOnly={viewModel.isFieldReadOnlyInd}>
                                        <Neo.AutoCompleteDropDown
                                            minCharacters={3}
                                            delayMs={1500}
                                            className="customAutoCompleteDropDown"
                                            bind={viewModel.clientAccountModel.meta.clientId}
                                            bindDisplay={viewModel.clientAccountModel.meta.clientNumber}
                                            displayMember={"clientDescription"}
                                            onItemSelected={() => viewModel.setExistingClientAccountFields(viewModel.clientAccountModel.clientNumber)}
                                            items={viewModel.filteredClientAccounts}
                                            itemSource={async (searchText) => viewModel.clientAccounts.filter(clientAccount => clientAccount.clientDescription.toLowerCase().includes(searchText.toLowerCase()))}
                                        />
                                    </Neo.FormGroup>
                                </div>

                            </div>
                        }
                        <div className="row">
                            <div className="col-lg-3 col-md-3">
                                <Neo.FormGroup
                                    label={TextConstants.Labels.ClientNumber}
                                    isReadOnly={viewModel.isFieldReadOnlyInd}
                                    bind={viewModel.clientAccountModel.meta.clientNumber}
                                />
                            </div>
                            <div className="col-lg-2 col-md-2">
                                {viewModel.currencyComponentVM.staticCurrencyLookupList.length > 0 &&
                                    <CurrencyComponent viewModel={viewModel.currencyComponentVM} label={TextConstants.Labels.ClientAccountCurrency} />}
                            </div>
                            <div className="col-lg-4 col-md-4">
                                <Neo.FormGroup
                                    label={TextConstants.Labels.ClientName}
                                    isReadOnly={viewModel.isFieldReadOnlyInd}
                                    bind={viewModel.clientAccountModel.meta.clientName} />
                            </div>
                            <div className="col-lg-3 col-md-3">
                                <Neo.FormGroup
                                    bindContext={viewModel.clientAccountModel.meta.clientEntityTypeName}
                                    label={TextConstants.Labels.ClientEntityType}
                                    isReadOnly={viewModel.isFieldReadOnlyInd}>
                                    <Neo.AutoCompleteDropDown
                                        itemSource={(searchValue: string) => viewModel.clientAccountApiClient.getClientEntityTypeListByCriteria(searchValue)}
                                        bind={viewModel.clientAccountModel.meta.clientEntityTypeId}
                                        bindDisplay={viewModel.clientAccountModel.meta.clientEntityTypeName}
                                        displayMember={"clientEntityTypeName"}
                                        items={viewModel.clientAccountApiClient.getClientEntityTypeListByCriteria(" ").then((arr) => arr.data.slice(0, 10)
                                            .sort((x, y) => x.clientEntityTypeName.localeCompare(y.clientEntityTypeName)))}
                                    />
                                </Neo.FormGroup>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-lg-2 col-md-2">
                                {viewModel.brokerList.length > 0 &&
                                    <Neo.FormGroup
                                        label={TextConstants.Labels.Broker}
                                        bindContext={viewModel.clientAccountModel.meta.stockbrokerName}>
                                        <Neo.AutoCompleteDropDown
                                            itemSource={viewModel.serviceProviderApiClient.getServiceProviderFileGroupingListByCriteria}
                                            bind={viewModel.clientAccountModel.meta.staticServiceProviderFileGroupingId}
                                            bindDisplay={viewModel.clientAccountModel.meta.stockbrokerName}
                                            displayMember={"serviceProviderGroupingName"}
                                            isReadOnly={viewModel.missingAccountsReadOnlyInd}
                                            onChange={() => viewModel.populateStockbrokerCodeField()}
                                            items={viewModel.serviceProviderApiClient.getServiceProviderFileGroupingListByCriteria(" ").then((arr) => arr.data.slice(0,10)
                                            .sort((x,y)=> x.serviceProviderGroupingName.localeCompare(y.serviceProviderGroupingName)))} />
                                    </Neo.FormGroup>
                                }
                            </div>
                            <div className="col-lg-3 col-md-3">
                                <Neo.FormGroup
                                    label={TextConstants.Labels.AccountNumber}
                                    isReadOnly={viewModel.missingAccountsReadOnlyInd || viewModel.clientAccountModel.editingClientAccount}
                                    bind={viewModel.clientAccountModel.meta.serviceProviderAccountNumber} />
                            </div>
                            <div className="col-lg-5 col-lg-5">
                                <Neo.FormGroup
                                    label={TextConstants.Labels.BrokerCode}
                                    isReadOnly={viewModel.missingAccountsReadOnlyInd || viewModel.clientAccountModel.editingClientAccount}
                                    bind={viewModel.clientAccountModel.meta.brokerDescription} />
                            </div>
                            <div className="col-lg-2 col-lg-2">
                                <Neo.FormGroup
                                    bind={viewModel.clientAccountModel.meta.activeInactive}
                                    label={TextConstants.Labels.Status}
                                    select={{ items: EnumHelper.asList(StatusDropDownItems), deSelectText: "Please select an option" }}
                                />
                            </div>
                        </div>
                        <div className="row customMarginTop29 float-right pr-3">
                            <Neo.Button
                                variant="primary" className="ml-3 col-md-2 col-lg-2" isSubmit
                            >{TextConstants.Buttons.Save}</Neo.Button>
                            <Neo.Button className="ml-3 col-md-2 col-lg-2"
                                onClick={() => this.props.onBack()}
                                variant="secondary"
                            >{TextConstants.Buttons.Cancel}</Neo.Button>
                        </div>
                    </Neo.Form>
                </Neo.Card>
            </React.Fragment>
        );
    }
    private onFormInvalid(e: React.FormEvent, justValidated?: boolean) {
        if (!justValidated) {
            ModalUtils.showMessage("Save", TextConstants.Validation.InvalidClientMapping);
        }
    }
}